import React, { useState, useEffect, useRef } from 'react';
import Editor from 'src/components/Editor/EventEditor';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useVideosActions } from 'src/store/videos/actions';
import { TimeZones } from 'src/components/utilities/timeZone';
import TimePicker from 'rc-time-picker';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ImageStringConverter,
    miliSecondsToHourMinute,
    defaultDateFormatter,
    getMomentDateFormat,
    minutesToHourMinutes
} from 'src/components/utilities/utils';
import moment from 'moment';
import { useEventActions } from 'src/store/event/actions';

const EventDetails = ({ editEventClicked, newVideoData, setCurrentStep }: any) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const eventActions = useEventActions(dispatch)
    const videoAction = useVideosActions(dispatch);
    const { eventId } = useParams<string>();
    const datePickerRef = useRef<any>(null);
    const [startTime, setStartTime] = useState<string>('00:00');
    const [descriptionEditorState, setDescriptionEditorState] = useState<string>('');
    const [eventInDaysData, setEventInDaysData] = useState<number>(0);
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
        control,
        setValue,
        setError
    } = useForm();
    const {
        singleEvent,
        mediaUrl,
        liveStreamId,
        isVideoUploaded,
        defaultDateFormat
    } = useSelector((state: RootState) => {
        return {
            singleEvent: state.event.singleEvent,
            mediaUrl: state.videos.mediaUrl,
            liveStreamId: state.videos.liveStreamId,
            isVideoUploaded: state.videos.isVideoUploaded,
            defaultDateFormat: state.login.defaultDateFormat
        };
    });
    useEffect(() => {
        if (mediaUrl) {
            setDescriptionEditorState(mediaUrl);
        }
    }, [mediaUrl]);
    useEffect(() => {
        if (singleEvent && Object.keys(singleEvent).length) {
            videoAction.getMediaUrlRequest(singleEvent.description);
        }
    }, [singleEvent]);
    useEffect(() => {
        if (singleEvent) {
            reset({
                ...singleEvent,
                isVideoChannelMembers: singleEvent.isVideoChannelMembers ?? true,
                displayDate: singleEvent.displayDate
                    ? moment(
                        singleEvent.displayDate,
                        getMomentDateFormat(),
                        true
                    ).toDate()
                    : '',
                duration: singleEvent.eventInDays
                    ? singleEvent.duration &&
                    moment
                        .duration(singleEvent.duration, 'minutes')
                        .days()
                    : singleEvent.duration &&
                    minutesToHourMinutes(singleEvent.duration),
                timeZoneId:
                    singleEvent.timeZoneId === null
                        ? defaultDateFormat.timeZoneId
                        : singleEvent.timeZoneId
            });

            setStartTime(singleEvent.displayTime);
            setEventInDaysData(singleEvent.eventInDays ? 1 : 0);
            setDescriptionEditorState(singleEvent.description ? singleEvent.description : '');
            setValue('timezoneFormat', singleEvent.timezoneFormat ? singleEvent.timezoneFormat : 2)
            // setSelectedVideoId(singleEvent.selectedVideo);
        }
    }, [singleEvent]);

    useEffect(() => {
        if (newVideoData) {
            setValue('name', newVideoData?.title)
        }
    }, [newVideoData])
    const onChange = (e: any) => {
        setStartTime(moment(e).format('HH:mm'));
    };
    const SectionHeader = ({ children }: { children: React.ReactNode }) => (
        <div className="bg-indigo-50 text-sm font-semibold text-gray-800 px-4 py-2 rounded-md">{children}</div>
    );
    const onSubmit = (data: any) => {
        if (!data.eventInDays && data.duration === '00:00') {
            setError('duration', {
                type: 'manual',
                message: 'Duration cannot be 00:00. Please enter a valid duration.'
            });
            return;
        }
        const payloadData = {
            id: data.id,
            patchJsonData: [
                { op: 'replace', path: 'name', value: data.name },
                { op: 'replace', path: 'title', value: data.title },
                { op: 'replace', path: 'description', value: ImageStringConverter(descriptionEditorState) },
                { op: 'replace', path: 'displayTime', value: startTime },
                { op: 'replace', path: 'duration', value: data.duration },
                { op: 'replace', path: 'displayDate', value: defaultDateFormatter(data.displayDate) },
                { op: 'replace', path: 'eventRegistrationSuccessMessage', value: data.eventRegistrationSuccessMessage },
                { op: 'replace', path: 'displayEventOverview', value: data.displayEventOverview },
                { op: 'replace', path: 'loginHeaderText', value: data.loginHeaderText },
                { op: 'replace', path: 'registrationHeaderText', value: data.registrationHeaderText },
                // { op: 'replace', path: 'selectedVideo', value: selectedVideoId },
                { op: 'replace', path: 'timeZoneId', value: data.timeZoneId },
                { op: 'replace', path: 'timezoneFormat', value: data.timezoneFormat },
                { op: 'replace', path: 'isVideoChannelMembers', value: data.isVideoChannelMembers },
            ]
        };
        eventActions.updateEventRequest(payloadData);
        setCurrentStep(4)
    };
    useEffect(() => {
        if (newVideoData) {
            setValue('duration', newVideoData?.duration);
            setValue('displayDate', newVideoData?.date)
            setStartTime(newVideoData?.time);
        }
    }, [isVideoUploaded, newVideoData]);

    return (

        <>
            <main className="flex-1 flex flex-col min-w-0">
                {errors.duration && (
                    <p className="text-danger">
                        {errors.duration.message}
                    </p>
                )}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-3 px-4 sm:px-8 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
                            <div className="min-w-0">
                                <h2 className="text-lg font-semibold text-gray-900 m-0">Event Details</h2>
                                <p className="text-sm text-gray-400 mt-1">
                                    Add the key details for your event. These details will be displayed on the registration form and event page.
                                </p>
                            </div>
                            <button type="button" onClick={() => { navigate('/events/event') }} className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                                Exit
                            </button>
                        </div>

                        <div className="px-4 sm:px-8 py-5 sm:py-6 space-y-6">
                            {/* Basic Information */}
                            <div>
                                <SectionHeader>Basic Information</SectionHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Event Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="name"
                                            {...register('name', {
                                                required: true
                                            })}
                                        />
                                        {errors.name?.type === 'required' && (
                                            <p className="text-red-700"> Required Field</p>
                                        )}

                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Event Title <span className="text-gray-400 font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="title"
                                            {...register('title')}
                                        />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                        Description <span className="text-red-500">*</span>
                                    </label>

                                    <div className="w-full max-w-full overflow-x-auto">
                                        <Editor
                                            editorState={descriptionEditorState}
                                            setEditorState={setDescriptionEditorState}
                                        />
                                    </div>

                                </div>
                            </div>

                            {/* Date & Time */}
                            <div>
                                <SectionHeader>Date &amp; Time</SectionHeader>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Event Date <span className="text-red-500">*</span>
                                        </label>

                                        <Controller
                                            name={'displayDate'}
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: { onChange, value } }) => (
                                                <div className="relative">
                                                    <DatePicker
                                                        placeholderText={(localStorage.getItem('defaultDateFormat') || 'MM/dd/yyyy').toUpperCase()}
                                                        selected={value && new Date(Date.parse(value))}
                                                        dateFormat={localStorage.getItem('defaultDateFormat') || 'MM/dd/yyyy'}
                                                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                                        onChange={(val: any) => onChange(val)}
                                                        ref={(r: any) => (datePickerRef.current = r)}
                                                    />
                                                    <i
                                                        onClick={() => {
                                                            if (datePickerRef.current) {
                                                                if (!datePickerRef.current.state.open) {
                                                                    datePickerRef.current.setOpen(true);
                                                                }
                                                            }
                                                        }}
                                                        className="fas fa-calendar-week absolute top-3 right-3"
                                                    />
                                                </div>
                                            )}
                                        />
                                        {errors.displayDate && (
                                            <p className="text-red-700">Required Field</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Start Time <span className="text-red-500">*</span>
                                        </label>

                                        <TimePicker
                                            className="w-full  "
                                            onChange={(e) => onChange(e)}
                                            value={startTime ? moment(startTime, 'HH:mm') : undefined}
                                            showSecond={false}
                                            use12Hours={true}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Timezone Format <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                            {...register('timezoneFormat')}>
                                            <option value={0}>Select Timezone Format</option>
                                            <option value={1}>Abbreviation</option>
                                            <option value={2}>GMT</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4 items-start">
                                    <div className="flex items-center gap-6 pt-1 md:col-span-2 xl:col-span-1">

                                        <div className="flex items-center">
                                            <div className="flex items-center">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input radio-btn"
                                                        type="radio"
                                                        checked={eventInDaysData === 0 ? true : false}
                                                        onChange={() => {
                                                            setEventInDaysData(0), setValue('duration', '01:00');
                                                        }}
                                                    />
                                                    <label className="whitespace-nowrap" htmlFor="singleday">
                                                        Single Day
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="flex items-center ml-2">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input radio-btn"
                                                        type="radio"
                                                        checked={eventInDaysData === 1 ? true : false}
                                                        onChange={() => {
                                                            setEventInDaysData(1), setValue('duration', '1');
                                                        }}
                                                    />
                                                    <label className="whitespace-nowrap" htmlFor="multipldays">
                                                        Multiple Days
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`form-group`}>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Timezone
                                        </label>
                                        <select
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                            {...register('timeZoneId')}>
                                            {TimeZones &&
                                                TimeZones.map((timezone, index) => {
                                                    return (
                                                        <option key={index} value={timezone.value}>
                                                            {timezone.label}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5"> Duration {eventInDaysData ? '(no.of days)' : '(hh:mm)'}</label>
                                        <input
                                            type="text"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="duration"
                                            {...register('duration', {
                                                validate: (value) => {
                                                    if (!eventInDaysData && value === '00:00') {
                                                        return 'Duration cannot be 00:00. Please enter a valid duration.';
                                                    }
                                                    return true;
                                                }
                                            })}
                                            placeholder={eventInDaysData ? '' : 'hh:mm'}
                                        />
                                        {errors.duration && (
                                            <p className="text-red-700">{errors.duration.message as string}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Event Page Content */}
                            <div>
                                <SectionHeader>
                                    Event Page Content <span className="text-gray-400 font-normal">(Optional)</span>
                                </SectionHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Registration Heading</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Register now to secure your place"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="registrationHeaderText"
                                            {...register('registrationHeaderText')} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Login Header</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Access the live event here"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="loginHeaderText"
                                            {...register('loginHeaderText')}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 items-start">


                                    <div className='flex items-start'>
                                        <div className={`form-group flex items-start gap-3 pt-1 form-switch pl-0 flex-shrink-0`}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input switch-btn"
                                                id="displayEventOverview"
                                                {...register('displayEventOverview')}
                                            />
                                        </div>
                                        <span className="ml-1">
                                            <span className="block text-sm font-semibold text-gray-700">Display event overview</span>
                                            <span className="block text-xs text-gray-400 mt-0.5">Show the event overview section on the event page.</span>
                                        </span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Registration Confirmation Message</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Thank you for registering. You will receive a confirmation email shortly."
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            id="eventRegistrationSuccessMessage"
                                            {...register('eventRegistrationSuccessMessage')}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Security Settings */}
                            <div>
                                <SectionHeader>Security Settings</SectionHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 items-start">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Access level for registered users <span className="text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="isVideoChannelMembers"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <select
                                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                                    value={value ? 'library' : 'event-only'}
                                                    onChange={(e) => onChange(e.target.value === 'library')}
                                                >
                                                    <option value="library">Library User (recommended)</option>
                                                    <option value="event-only">Event Only</option>
                                                </select>
                                            )}
                                        />
                                    </div>

                                    <div className="bg-indigo-50 rounded-lg p-4 flex items-start gap-2">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0 text-indigo-600">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="16" x2="12" y2="12" />
                                            <line x1="12" y1="8" x2="12.01" y2="8" />
                                        </svg>
                                        <div className="text-xs text-gray-600 space-y-2">
                                            <p className="m-0 font-semibold text-gray-800">Access level options</p>
                                            <p className="m-0">
                                                <span className="font-semibold text-gray-800">Library User (recommended)</span>
                                                <br />
                                                Registered users will have a standard library account and can access your full content library (based on
                                                your membership settings), as well as this event.
                                            </p>
                                            <p className="m-0">
                                                <span className="font-semibold text-gray-800">Event Only</span>
                                                <br />
                                                Registered users can only access this event. They will not have access to your content library.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1" />

                        {/* Footer */}
                        <div className="flex justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                            <button
                                onClick={() => { setCurrentStep(2); videoAction.resetVideoLibraryRequest(); }}
                                type="button"
                                className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-4 sm:px-5 py-2.5 hover:bg-gray-50">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back
                            </button>
                            <button
                                type="submit"
                                className="flex-1 sm:flex-none justify-center flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-4 sm:px-5 py-2.5">
                                Next: Registration Setup
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </form>
            </main>
        </>
    );
};

export default EventDetails;